import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Send, MapPin, Calendar, ExternalLink, Clock, CheckCircle2, XCircle, Loader2, Briefcase, Sparkles, TrendingUp, Zap, Eye, ChevronDown, ChevronUp } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { getCandidateProfile, updateCandidate, getSubscription, getSubscriptionPlanList } from '../services/api';
import { normalizePlan, FALLBACK_PLANS, formatPrice } from '../data/plans';
import SEO from '../components/SEO';
import BackButton from '../components/BackButton';
import PurchaseModal from '../components/PurchaseModal';
import './ApplicationsPage.css';

const ApplicationsPage = () => {
  const { user } = useAuth();
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [expandedId, setExpandedId] = useState(null);
  const [autoApplyEnabled, setAutoApplyEnabled] = useState(false);
  const [consentGiven, setConsentGiven] = useState(false);
  const [showConsent, setShowConsent] = useState(false);
  const [autoApplySaving, setAutoApplySaving] = useState(false);
  const [autoApplyError, setAutoApplyError] = useState('');
  const [showPurchase, setShowPurchase] = useState(false);
  const [subscription, setSubscription] = useState(null);
  const [plans, setPlans] = useState(FALLBACK_PLANS);
  const [planLoading, setPlanLoading] = useState(true);

  // Mevcut ayarı DB'den oku, toggle sayfa açılışında gerçek durumu göstersin
  useEffect(() => {
    if (!user?.candidateId) return;
    getCandidateProfile()
      .then((c) => {
        const enabled = Boolean(c?.isAutoApplyEnabled);
        setAutoApplyEnabled(enabled);
        // DB'de aktifse onay daha önce verilmiş demektir
        if (enabled) setConsentGiven(true);
      })
      .catch(() => {});
  }, [user?.candidateId]);

  // Aktif abonelik + paket listesi, kota ve mevcut paket buradan gelir
  const loadSubscription = useCallback(() => {
    if (!user?.candidateId) {
      setPlanLoading(false);
      return;
    }
    setPlanLoading(true);
    Promise.all([
      getSubscription(user.candidateId).catch(() => null),
      getSubscriptionPlanList().catch(() => null),
    ])
      .then(([sub, list]) => {
        setSubscription(sub || null);
        const normalized = (list || []).map(normalizePlan).sort((a, b) => a.sortingId - b.sortingId);
        if (normalized.length) setPlans(normalized);
      })
      .finally(() => setPlanLoading(false));
  }, [user?.candidateId]);

  useEffect(() => { loadSubscription(); }, [loadSubscription]);

  useEffect(() => {
    const fetchApplications = async () => {
      setLoading(true);
      try {
        // TODO: API entegrasyonu, GET /api/AutoApply/GetApplications
        await new Promise(r => setTimeout(r, 800));
        setApplications([]);
      } catch {
        setApplications([]);
      } finally {
        setLoading(false);
      }
    };
    fetchApplications();
  }, []);

  const statusConfig = {
    applied: { label: 'Başvuruldu', icon: CheckCircle2, color: '#00C9A7', bg: 'rgba(0, 201, 167, 0.08)' },
    pending: { label: 'Beklemede', icon: Clock, color: '#D97706', bg: 'rgba(217, 119, 6, 0.08)' },
    viewed: { label: 'Görüntülendi', icon: Eye, color: '#2F3A8F', bg: 'rgba(47, 58, 143, 0.08)' },
    interview: { label: 'Mülakata Davet', icon: Sparkles, color: '#8B5CF6', bg: 'rgba(139, 92, 246, 0.08)' },
    accepted: { label: 'Kabul Edildi', icon: CheckCircle2, color: '#059669', bg: 'rgba(5, 150, 105, 0.08)' },
    rejected: { label: 'Olumsuz', icon: XCircle, color: '#DC2626', bg: 'rgba(220, 38, 38, 0.08)' },
  };

  const filtered = filter === 'all' ? applications : applications.filter(a => a.status === filter);

  const formatDate = (dateStr) => {
    try {
      return new Date(dateStr).toLocaleDateString('tr-TR', { day: 'numeric', month: 'long', year: 'numeric' });
    } catch {
      return dateStr;
    }
  };

  // Aktif paket: abonelik yoksa ücretsiz paket geçerli
  const freePlan = plans.find((p) => p.isFree) || plans[0];
  const activePlan = plans.find((p) => p.id === subscription?.planId) || freePlan;
  // Plan listesindeki ad Türkçeleştirilmiş; BE'nin planName'i (İngilizce) yedek
  const matchedPlan = plans.find((p) => p.id === subscription?.planId);
  const planName = matchedPlan?.name || subscription?.planName || activePlan?.name || 'Başlangıç';
  const isPaidPlan = Boolean(subscription) && !activePlan?.isFree;

  // Kota: sınırsız pakette limit yok
  const quotaLimit = activePlan?.isUnlimited ? null : (activePlan?.creditCount ?? 5);
  const usedThisPeriod = applications.length;
  const quotaLeft = quotaLimit === null ? null : Math.max(0, quotaLimit - usedThisPeriod);
  const quotaPercent = quotaLimit === null || quotaLimit === 0
    ? 100
    : Math.round((quotaLeft / quotaLimit) * 100);

  // Stats
  const totalApplied = applications.length;
  const totalViewed = applications.filter(a => a.status === 'viewed' || a.status === 'interview' || a.status === 'accepted').length;

  // Ayarı DB'ye yaz, iyimser güncelleme, hata olursa geri al
  const persistAutoApply = async (next) => {
    if (!user?.candidateId) {
      setAutoApplyError('Önce profilinizi tamamlamanız gerekiyor.');
      return;
    }
    const previous = autoApplyEnabled;
    setAutoApplyEnabled(next);
    setAutoApplySaving(true);
    setAutoApplyError('');
    try {
      const res = await updateCandidate({ isAutoApplyEnabled: next });
      if (res && res.status === false) throw new Error(res.message || 'Kaydedilemedi');
    } catch {
      setAutoApplyEnabled(previous);
      setAutoApplyError('Ayar kaydedilemedi. Lütfen tekrar deneyin.');
    } finally {
      setAutoApplySaving(false);
    }
  };

  const handleEnableAutoApply = () => {
    if (autoApplySaving) return;
    // Onay yalnızca aktif ederken sorulur; kapatmak için gerekmez
    if (!autoApplyEnabled && !consentGiven) {
      setShowConsent(true);
      return;
    }
    persistAutoApply(!autoApplyEnabled);
  };

  const handleConsent = () => {
    setConsentGiven(true);
    setShowConsent(false);
    persistAutoApply(true);
  };

  return (
    <div className="applications-page">
      <SEO
        title="Başvurularım"
        description="Yapay zeka ile yapılan iş başvurularınızı takip edin."
        path="/panel/basvurularim"
        noindex
      />
      <div className="applications__container">
        <BackButton />

        <div className="applications__header">
          <div className="applications__header-icon">
            <Send size={22} />
          </div>
          <div>
            <h1 className="applications__title">Başvurularım</h1>
            <p className="applications__subtitle">Yapay zekanın senin için eşleştirdiği ilanları takip et.</p>
          </div>
        </div>

        {/* Auto Apply Toggle Card */}
        <div className={`applications__auto-card ${autoApplyEnabled ? 'applications__auto-card--active' : ''}`}>
          <div className="applications__auto-top">
            <div className="applications__auto-info">
              <div className={`applications__auto-dot ${autoApplyEnabled ? 'applications__auto-dot--on' : ''}`} />
              <div>
                <span className="applications__auto-label">Akıllı Eşleştirme</span>
                <span className={`applications__auto-status ${autoApplyEnabled ? 'applications__auto-status--on' : ''}`}>
                  {autoApplyEnabled ? 'Aktif' : 'Pasif'}
                </span>
              </div>
            </div>
            <button
              className={`applications__auto-toggle ${autoApplyEnabled ? 'applications__auto-toggle--on' : ''}`}
              onClick={handleEnableAutoApply}
              disabled={autoApplySaving}
              aria-pressed={autoApplyEnabled}
              aria-label="Akıllı eşleştirme durumu"
            >
              <span className="applications__auto-toggle-thumb" />
            </button>
          </div>
          <p className="applications__auto-desc">
            {autoApplyEnabled
              ? 'Yapay zeka, profilinizle yüksek eşleşen ilanları sizin için buluyor.'
              : 'Siz uyurken yapay zeka profilinizle yüksek eşleşen ilanları sizin için bulsun.'}
          </p>

          {autoApplyError && (
            <p className="applications__auto-error">
              <XCircle size={14} /> {autoApplyError}
            </p>
          )}

          {/* Paket ve kalan hak */}
          <div className="applications__quota">
            <div className="applications__quota-info">
              <div className="applications__quota-header">
                <span className="applications__quota-label">
                  {planLoading ? 'Paket yükleniyor...' : (
                    <>
                      <strong className="applications__plan-name">{planName}</strong>
                      {isPaidPlan && activePlan?.price > 0 && (
                        <span className="applications__plan-price">
                          {' · '}{formatPrice(activePlan.price, activePlan.currency)}/{activePlan.period}
                        </span>
                      )}
                    </>
                  )}
                </span>
                <span className="applications__quota-count">
                  {planLoading ? '-'
                    : quotaLimit === null ? 'Sınırsız'
                      : `${quotaLeft} / ${quotaLimit}`}
                </span>
              </div>
              <div className="applications__quota-bar">
                <div className="applications__quota-fill" style={{ width: `${quotaPercent}%` }} />
              </div>
              <span className="applications__quota-sub">
                {quotaLimit === null
                  ? 'Eşleşen tüm ilanlar için form taslağı hazırlanabilir'
                  : `Bu ay kalan kullanım hakkın${quotaLeft === 0 ? ' bitti' : ''}`}
                {subscription?.nextPaymentDate && (
                  <> · Yenilenme: {formatDate(subscription.nextPaymentDate)}</>
                )}
              </span>
            </div>
            <button className="applications__quota-upgrade" onClick={() => setShowPurchase(true)}>
              {isPaidPlan ? 'Paketi Değiştir' : 'Paketi Yükselt'}
            </button>
          </div>
        </div>

        {/* Stats */}
        {totalApplied > 0 && (
          <div className="applications__stats">
            <div className="applications__stat">
              <Send size={18} className="applications__stat-icon" style={{ color: '#2F3A8F' }} />
              <div>
                <span className="applications__stat-value">{totalApplied}</span>
                <span className="applications__stat-label">Toplam Başvuru</span>
              </div>
            </div>
            <div className="applications__stat">
              <Eye size={18} className="applications__stat-icon" style={{ color: '#00C9A7' }} />
              <div>
                <span className="applications__stat-value">{totalViewed}</span>
                <span className="applications__stat-label">Görüntülenen</span>
              </div>
            </div>
          </div>
        )}

        {/* Filters */}
        <div className="applications__filters">
          {[
            { key: 'all', label: 'Tümü' },
            { key: 'applied', label: 'Gönderilen Başvurular' },
            { key: 'viewed', label: 'Görüntülendi' },
          ].map(f => (
            <button
              key={f.key}
              className={`applications__filter ${filter === f.key ? 'applications__filter--active' : ''}`}
              onClick={() => setFilter(f.key)}
            >
              {f.label}
            </button>
          ))}
        </div>

        {/* Content */}
        {loading ? (
          <div className="applications__loading">
            <Loader2 size={24} className="applications__spinner" />
            <p>Başvurular yükleniyor...</p>
          </div>
        ) : filtered.length === 0 ? (
          <div className="applications__empty">
            <Briefcase size={44} />
            <h3>{filter === 'all' ? 'Henüz başvurun yok' : 'Bu kategoride başvuru bulunamadı'}</h3>
            <p>
              {filter === 'all'
                ? autoApplyEnabled
                  ? 'Yapay zeka uygun ilanları arıyor. Form taslakları hazırlandıkça burada listelenecek.'
                  : 'Akıllı eşleştirmeyi aktif edin, yapay zeka sizin için uygun ilanları bulup form taslağı hazırlasın.'
                : 'Diğer filtreleri deneyebilirsiniz.'}
            </p>
            {filter === 'all' && !autoApplyEnabled && (
              <div className="applications__empty-actions">
                <button className="applications__cta applications__cta--primary" onClick={handleEnableAutoApply}>
                  <Zap size={16} /> Yazılımı Başlat
                </button>
              </div>
            )}
          </div>
        ) : (
          <div className="applications__list">
            {filtered.map((app) => {
              const status = statusConfig[app.status] || statusConfig.applied;
              const StatusIcon = status.icon;
              const isExpanded = expandedId === app.id;

              return (
                <div key={app.id} className={`applications__card ${isExpanded ? 'applications__card--expanded' : ''}`}>
                  <button className="applications__card-header" onClick={() => setExpandedId(isExpanded ? null : app.id)}>
                    <div className="applications__card-main">
                      <div className="applications__card-left">
                        <h3 className="applications__card-title">{app.jobTitle}</h3>
                        <div className="applications__card-meta">
                          {app.city && (
                            <span><MapPin size={13} /> {app.city}</span>
                          )}
                          <span><Calendar size={13} /> {formatDate(app.appliedAt)}</span>
                          {app.matchScore && (
                            <span className="applications__card-score">
                              <TrendingUp size={13} /> %{app.matchScore} uyum
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="applications__card-right">
                        <div className="applications__card-status" style={{ background: status.bg, color: status.color }}>
                          <StatusIcon size={13} />
                          {status.label}
                        </div>
                        <div className="applications__card-chevron">
                          {isExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                        </div>
                      </div>
                    </div>
                  </button>

                  {isExpanded && (
                    <div className="applications__card-body">
                      {app.jobDescription && (
                        <div className="applications__card-desc">
                          <p>{app.jobDescription}</p>
                        </div>
                      )}
                      <div className="applications__card-footer">
                        {app.jobUrl && (
                          <a href={app.jobUrl} target="_blank" rel="noopener noreferrer" className="applications__card-link">
                            <ExternalLink size={13} /> İlan Detayı
                          </a>
                        )}
                        {app.appliedCvName && (
                          <span className="applications__card-cv">
                            CV: {app.appliedCvName}
                          </span>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* Consent Modal */}
        {showConsent && (
          <div className="applications__consent-overlay" onClick={() => setShowConsent(false)}>
            <div className="applications__consent" onClick={e => e.stopPropagation()}>
              <h3 className="applications__consent-title">Dijital Temsil ve Otomasyon Açık Rıza Onayı</h3>
              <div className="applications__consent-body">
                <p>
                  Platform üzerinde tanımladığınız bilgiler doğrultusunda, kendi adınıza kamu
                  veya özel sektör iş ilan sistemlerinde form taslağı hazırlama adımlarını
                  yürütmek üzere isBulsun yazılımına dijital temsil yetkisi verdiğinizi kabul ve
                  beyan ediyorsunuz. Bu kapsamda yazılım:
                </p>
                <ul>
                  <li>Profilinize uygun iş ilanlarını bulur ve eşleştirir</li>
                  <li>CV ve profil bilgilerinizi kullanarak sizin adınıza form taslağı hazırlar; gönderim yalnızca sizin onayınızla olur</li>
                  <li>Form durumlarını takip eder ve size raporlar</li>
                  <li>İlana özel CV optimizasyonu yapar</li>
                </ul>
                <p className="applications__consent-note">
                  Bu hizmet bir işe yerleştirme garantisi veya işe aracılık değil, Veri Analiz ve
                  Form Taslak Hazırlama Yazılımı lisansıdır; iş garantisi içermez. Haftalık 5
                  kullanım ücretsizdir. Bu onay dilediğiniz zaman geri alınabilir. Kişisel
                  verileriniz KVKK kapsamında korunmaktadır.
                </p>
              </div>
              <div className="applications__consent-actions">
                <button className="applications__consent-btn applications__consent-btn--primary" onClick={handleConsent}>
                  Onaylıyorum ve Aktif Et
                </button>
                <button className="applications__consent-btn applications__consent-btn--secondary" onClick={() => setShowConsent(false)}>
                  Vazgeç
                </button>
              </div>
            </div>
          </div>
        )}

        <PurchaseModal
          isOpen={showPurchase}
          onClose={() => setShowPurchase(false)}
          currentPlanId={activePlan?.id}
          onSuccess={loadSubscription}
        />
      </div>
    </div>
  );
};

export default ApplicationsPage;
